import { buildVersion, sourceBranch } from 'app/../environments/environment';
import { ILoggingContext } from 'app/interfaces/IPortalServiceResult';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { LogRow } from './logger.log-row';
import { AILocal, LoggerVariables } from './logger.common';
import { loggerConstants } from 'app/logger/logger.constants';
import { ILogger } from 'app/logger/ILogger';
import { GlobalVariables } from 'app/services/globals';
import { LoggerBaseService } from './logger-base.service';
import {
    isAdfProdEnvironment, isCIEnvironment, isDevEnvironment, isStagingEnvironment,
    isUsSecEnvironment,
    isMooncakeEnvironment, isFairfaxEnvironment, isUsNatEnvironment,
    isAdfMooncakeEnvironment, isAdfBleuEnvironment,
    isAdfFairfaxEnvironment
} from 'environments/environment.util';
import { Injector } from '@angular/core';
import { EventRow } from './logger.event-row';
import { PageViewRow } from './logger.page-row';
import { isEURegion } from 'app/shared/utils/region.util';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';
import { TraceRow } from './logger.trace-row';
import { distinctUntilChanged, filter } from 'rxjs'
import { merge } from 'lodash';

/**
 * This inner AdfLoggerService needs to handle the log info from both root context or nonRoot context
 */
export class AdfLoggerService extends LoggerBaseService implements ILogger {
    constructor(
        _injector: Injector,
        // in root context, _nullableUrlService would be undefined
        private readonly _nullableUrlService?: FactoryUrlInformationService
    ) {
        super(_injector);
        this._contextMode = !!this._nullableUrlService ? 'subContext' : 'root';

        if (isDevEnvironment() || GlobalVariables.enableIFrameMode) {
            oneDSWeb.addTelemetryInitializer(() => false);
        }
    }

    public logEvent(event: EventRow): void {
        // this._addMaskedUserId(event);
        this._logTrace(event, this.events);
    }

    public logPageView(): void {
        const url = window.location.href.split('#')[0];
        const pageView = new PageViewRow(document.title, url);
        // this._addMaskedUserId(pageView);
        this._logTrace(pageView, this.pageViews);
    }

    public flush(): void {
        oneDSWeb?.getPostChannel()?.flush();
    }

    protected _logTrace(row: LogRow, buffer: LogRow[]): void {

        if (this._notSupportLogging()) {
            return;
        }

        row.addToProperties('buildVersion', buildVersion);
        row.addToProperties('resourceContextData', this._injector.get(RESOURCE_CONTEXT_DATA));
        if (this.isNonRootContext) {
            row.addToProperties('factoryVersion', this._nullableUrlService.isFactoryProvided ? GlobalVariables.dataFactoryVersion : '');
        } else {
            row.addToProperties('factoryVersion', '');
        }

        // Add our custom data here
        for (const [key, value] of this.trackingParameters) {
            row.addToProperties(key, value);
        }

        if (this.isNonRootContext) {
            const user = this._cachedUser;
            row.addToProperties('tenantId', user?.profile?.tid || '');
        } else {
            row.addToProperties('tenantId', '');
        }

        if (LoggerVariables.isGitMode.value) {
            row.addToProperties('mode', loggerConstants.GitMode);
            row.addToProperties('gitProvider', LoggerVariables.gitProvider);
        } else {
            row.addToProperties('mode', loggerConstants.LiveMode);
        }

        // Remove any sensitive strings from message and dynamicExpression before logging
        this._removeSensitiveStrings(row);

        if (isDevEnvironment()) {
            row.logToConsole();
        } else {
            if (!this.hasLoggingContextConfiged || !oneDSWeb.isInitialized()) {
                buffer.push(row);
            } else {
                row.pushToAppInsights()
            }
        }
    }

    protected _setGlobalLoggingContext(loggingContext: ILoggingContext): void {
        oneDSWeb.loggingContext = merge(oneDSWeb.loggingContext, loggingContext);
        oneDSWeb.loggingContext.releaseBranch = sourceBranch;
        oneDSWeb.loggingContext.buildVersion = buildVersion;
        if (this.isNonRootContext) {
            this._nullableUrlService.dataFactoryLocation$.pipe(
                filter((value) => !!value),
                distinctUntilChanged()
            ).subscribe((location: string) => {
                this._onLocationChange(location);
            });
            this._nullableUrlService.subId$.subscribe(() => {
                oneDSWeb.loggingContext.dataFactoryName = this._nullableUrlService.dataFactoryName;
                oneDSWeb.loggingContext.resourceGroupName = this._nullableUrlService.resourceGroupName;
                oneDSWeb.loggingContext.subscriptionId = this._nullableUrlService.subscriptionId;
            });
        }
    }

    protected override _flushBuffers(): void {
        if (!oneDSWeb.isInitialized()) {
            return;
        }
        super._flushBuffers();
    }

    private _notSupportLogging(): boolean {
        return GlobalVariables.enableIFrameMode;
    }

    private _onLocationChange(location: string): void {
        oneDSWeb.loggingContext.location = location;
        //currently, we only setup the location once, 
        //if later we support switch locations after app bootstrapped, we need to re-init the oneDSWeb
        if (!oneDSWeb.isInitialized()) {
            const config = {
                instrumentationKey: this._resolveIngestKey(),
                endpointUrl: this._resolveEndpointUrl(location),
                webAnalyticsConfiguration: {
                    isCookieUseDisabled: true,
                    disableAjaxTracking: false,
                    autoCapture: {
                        scroll: true,
                        pageView: true,
                        onLoad: true,
                        onUnload: true,
                        click: true,
                        resize: true,
                        jsError: true
                    }
                },
                propertyConfiguration: {
                    dropIdentifiers: true,
                },
            };
            // 1DS SDK initialization
            oneDSWeb.initialize(config, []);
            oneDSWeb.addTelemetryInitializer(function (envelope) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const telemetryItem = envelope.baseData as { [key: string]: any };
                telemetryItem.properties = telemetryItem.properties || {};
                telemetryItem.properties['loggingKey'] = oneDSWeb.loggingContext.loggingKey;
                telemetryItem.properties['subscriptionId'] = oneDSWeb.loggingContext.subscriptionId;
                telemetryItem.properties['location'] = oneDSWeb.loggingContext.location;
                telemetryItem.properties['resourceGroupName'] = oneDSWeb.loggingContext.resourceGroupName;
                telemetryItem.properties['dataFactoryName'] = oneDSWeb.loggingContext.dataFactoryName;
                telemetryItem.properties['releaseBranch'] = oneDSWeb.loggingContext.releaseBranch;
                telemetryItem.properties['buildVersion'] = oneDSWeb.loggingContext.buildVersion;
            });
            this._flushBuffers();
        } else {
            this._logTrace(new TraceRow('OneDSWeb is already initialized', AILocal.SeverityLevel.Error, 'AdfLoggerService'), this.traces);
        }

    }
    /*
    * https://eng.ms/docs/products/geneva/collect/clientingestion/onboardingsteps/endpoints/configureuploaddestination
    */
    private _resolveEndpointUrl(location: string): string {
        // todo: Task 4834731: Belu cloud
        if (isMooncakeEnvironment()) {
            return 'https://collector.azure.cn/OneCollector/1.0/';
        } else if (isFairfaxEnvironment()) {
            return 'https://tb.events.data.microsoft.com/OneCollector/1.0/'; // L4 
        } else if (isUsNatEnvironment()) {
            return 'https://collector.azure.eaglex.ic.gov/OneCollector/1.0/';
        } else if (isUsSecEnvironment()) {
            return 'https://collector.azure.microsoft.scloud/OneCollector/1.0/';
        } else if (isAdfBleuEnvironment()) {
            return 'https://collector.sovcloud-api.fr/OneCollector/1.0/';
        }
        return isEURegion(location) ? 'https://eu-mobile.events.data.microsoft.com/OneCollector/1.0/' : 'https://browser.events.data.microsoft.com/OneCollector/1.0/';
    }

    private _resolveIngestKey(): string {
        if (isCIEnvironment()) {
            return 'e43de3efca56455d96f79ba2b6c69b09-3947ca64-5723-4049-b7b9-eeed4882890c-7168';
        }
        if (isStagingEnvironment()) {
            return 'bfddb4108e424b2aadcf142b2c11fdab-c27343e4-dd19-4aa9-b390-1c2a1ade631d-7439';
        }
        if (isAdfProdEnvironment() || isAdfFairfaxEnvironment() || isAdfMooncakeEnvironment() || isAdfBleuEnvironment()) {
            return 'ae9df01b4a724ec386b1cbc241b558f5-d176d5fb-76e5-4b25-9066-71f099f5adae-7148';
        }
        return '';
    }

    // private _addMaskedUserId(row: LogRow): void {
    //     // only fully enable this when aria logger is on
    //     if (GlobalVariables.enableAriaLogger) {
    //         const user = this._cachedUser;
    //         row.addToProperties('localAccountId', user.profile && user.profile.oid ? user.profile.oid : '');
    //     }
    // }
}
